import { useMemo, useState } from 'react';
import {
  MetamodelContextSettings,
  MetamodelFieldContext,
} from 'metamodel/types';
import { FieldSubtitle } from '../atoms';
import { BreadCrumbNavigation } from '../breadCrumbNavigation/BreadCrumbNavigation';
import {
  getComponentsWithOrWithoutValueQueryBuilderQuery,
  getReferencesWithOrWithoutValueQuery,
} from './utils';
import { resolveFieldLabel } from '../utils';
import { GremlinSearchTableWithLoader as GremlinSearchTableWithLoaderBase } from '../GremlinSearchTableWithLoader';
import { WorkspaceNavigationTitle } from '../workspaceNavigationTitle/WorkspaceNavigationTitle';
import { APIFieldAttributes, Metamodel } from '@ardoq/api-types';
import { DisplayPreference } from './types';
import { SimpleBlockDiagramContentType } from '@ardoq/simple-block-diagram/types';
import { dateRangeOperations } from '@ardoq/date-range';
import { Paragraph } from '@ardoq/typography';
import { GroupFieldLayout, RadioGroup } from '@ardoq/forms';
import { Box } from '@ardoq/layout';
import { AdvancedSearchTableWithLoader as AdvancedSearchTableWithLoaderBase } from '../AdvancedSearchTable/AdvancedSearchTableWithLoader';
import { t, type I18nLocale } from '@ardoq/i18n';

const FieldStats = ({
  context,
  metamodel,
  isCheckboxField,
  nonTemplateFieldsByName,
  i18nLocale,
}: {
  context: MetamodelFieldContext & MetamodelContextSettings;
  metamodel: Metamodel;
  isCheckboxField: boolean;
  nonTemplateFieldsByName: Map<string, APIFieldAttributes>;
  i18nLocale: I18nLocale;
}) => {
  // Show non empty results if all fields are filled out
  const { instanceCount, fieldUsage } = context.metadata;

  const defaultDisplayPreference =
    instanceCount === fieldUsage[context.field]
      ? DisplayPreference.WITH_VALUE
      : DisplayPreference.WITHOUT_VALUE;

  const [displayPreference, setDisplayPreference] = useState<DisplayPreference>(
    defaultDisplayPreference
  );

  const dateRangeFieldMap = metamodel.metamodel.dateRangeFieldMap?.get(
    context.workspaceId
  );
  const isDateRangeField = Boolean(dateRangeFieldMap?.[context.field]);

  const displayPreferenceOptions = useMemo(
    () => [
      {
        value: DisplayPreference.WITH_VALUE,
        label: t(
          i18nLocale,
          'metamodel:stats.field.displayPreference.withValue',
          'With value'
        ),
      },
      {
        value: DisplayPreference.WITHOUT_VALUE,
        label: t(
          i18nLocale,
          'metamodel:stats.field.displayPreference.withoutValue',
          'Without value'
        ),
      },
    ],
    [i18nLocale]
  );

  const shouldUseAdvancedSearchQuery =
    context.type === SimpleBlockDiagramContentType.COMPONENT;

  return (
    <Box position="relative">
      <BreadCrumbNavigation
        metamodel={metamodel}
        context={context}
        nonTemplateFieldsByName={nonTemplateFieldsByName}
      />
      <Box marginTop="small">
        <WorkspaceNavigationTitle
          metamodel={metamodel}
          context={context}
          i18nLocale={i18nLocale}
        >
          {isDateRangeField
            ? dateRangeOperations.extractDateRangeFieldLabel(
                dateRangeFieldMap?.[context.field]?.start?.label ??
                  context.field
              )
            : resolveFieldLabel(context.field, nonTemplateFieldsByName)}
        </WorkspaceNavigationTitle>
      </Box>
      <Paragraph>
        {t(
          i18nLocale,
          'metamodel:stats.field.fieldId',
          { fieldId: context.field },
          'Field id: {{fieldId}}'
        )}
      </Paragraph>
      <FieldSubtitle context={context} i18nLocale={i18nLocale} />
      <Box marginTop="small" display="inline-block">
        <RadioGroup
          value={displayPreference}
          options={displayPreferenceOptions}
          onValueChange={value =>
            setDisplayPreference(value as DisplayPreference)
          }
          groupFieldLayout={GroupFieldLayout.HORIZONTAL}
        />
      </Box>
      {shouldUseAdvancedSearchQuery ? (
        <AdvancedSearchTableWithLoaderBase
          query={getComponentsWithOrWithoutValueQueryBuilderQuery(
            context,
            displayPreference,
            isCheckboxField
          )}
        />
      ) : (
        <GremlinSearchTableWithLoaderBase
          query={getReferencesWithOrWithoutValueQuery(
            context,
            metamodel,
            displayPreference,
            isCheckboxField,
            isDateRangeField
          )}
        />
      )}
    </Box>
  );
};

export { FieldStats };
